// Operator › Approvals SLA (`/operator/approvals?p=&sla=`; M5-05, 06 §15; Reports / Ops UX pass
// 2026-10-21): how fast approvals are decided against a target across the operator's companies
// (`GET /v1/operator/approvals-sla`, one call). The 56 px location header, a 40 px toolbar (week,
// target), four compact tiles, then the approvals waiting past the target (oldest first, Open
// approvals on hover), each company and each approver seat with its holders.
import {
  Badge,
  EmptyState,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toolbar,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  CheckCheckIcon,
  ClockAlertIcon,
  GaugeIcon,
  HourglassIcon,
  InboxIcon,
  TimerIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import type { Api } from "../../api/client";
import { formatDateTime, humanize } from "../format";
import {
  OpsBody,
  OpsFrame,
  operatorCrumb,
  RowActions,
  RowLinkAction,
  RowSub,
  TABLE_ROW_HOVER,
  Tiles,
} from "../ops-ux";
import { LoadError, Loading, Nothing, Section } from "../screen-parts";
import { CoverageNotice, WeekSelect } from "./agency-parts";
import {
  type ApprovalsSla,
  ageText,
  approvalsSlaQuery,
  SLA_CHOICES,
  slaLabel,
} from "./agency-queries";

type Stats = ApprovalsSla["totals"];

const withinPct = (s: Stats) =>
  s.decided ? `${Math.round((s.decided_within_sla / s.decided) * 100)}%` : "—";

export function ApprovalsSlaPage({
  api,
  week,
  sla,
  onChange,
}: {
  api: Api;
  week: string;
  sla: number;
  onChange: (next: { week: string; sla: number }) => void;
}) {
  const q = useQuery(approvalsSlaQuery(api, week, sla));
  const choices = SLA_CHOICES.includes(sla as (typeof SLA_CHOICES)[number])
    ? [...SLA_CHOICES]
    : [sla, ...SLA_CHOICES];
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Approvals SLA" }]}
        icon={TimerIcon}
        title="Approvals SLA"
        count={q.data ? q.data.totals.pending : undefined}
      />
      <Toolbar bleed>
        <WeekSelect week={week} onWeek={(w) => onChange({ week: w, sla })} />
        <NativeSelect
          size="sm"
          aria-label="SLA target"
          value={String(sla)}
          onChange={(e) => onChange({ week, sla: Number(e.target.value) })}
          className="w-auto"
        >
          {choices.map((m) => (
            <NativeSelectOption key={m} value={String(m)}>
              Target {slaLabel(m)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Toolbar>
      <OpsBody>
        {q.isPending ? (
          <Loading label="Loading the approvals SLA" rows={6} />
        ) : q.isError ? (
          <LoadError what="The approvals SLA" error={q.error} />
        ) : q.data.companies.length === 0 ? (
          <>
            <CoverageNotice complete={q.data.complete} reasons={q.data.incomplete_reasons} />
            <EmptyState
              variant="card"
              icon={InboxIcon}
              title="No approvals yet"
              description="Approvals your companies' digital employees raise show up here with their decision times."
            />
          </>
        ) : (
          <SlaBody data={q.data} week={week} />
        )}
      </OpsBody>
    </OpsFrame>
  );
}

function SlaBody({ data, week }: { data: ApprovalsSla; week: string }) {
  const t = data.totals;
  return (
    <>
      <CoverageNotice complete={data.complete} reasons={data.incomplete_reasons} />
      <Tiles data-slot="sla-totals" aria-label="Approvals SLA totals">
        <StatTile
          size="sm"
          label="Pending now"
          icon={HourglassIcon}
          value={t.pending}
          detail={`oldest ${ageText(t.oldest_pending_minutes)}`}
        />
        <StatTile
          size="sm"
          label="Over target"
          icon={ClockAlertIcon}
          value={t.pending_over_sla}
          detail={`waiting > ${slaLabel(data.sla_minutes)}`}
        />
        <StatTile
          size="sm"
          label="Within target"
          icon={GaugeIcon}
          value={withinPct(t)}
          detail={`${t.decided_within_sla} of ${t.decided} decided · ${t.expired} expired`}
        />
        <StatTile
          size="sm"
          label="Median decision"
          icon={CheckCheckIcon}
          value={ageText(t.median_minutes)}
          detail={`P90 ${ageText(t.p90_minutes)}`}
        />
      </Tiles>

      <Section id="sla-overdue" title="Waiting past the target">
        {data.overdue.length === 0 ? (
          <Nothing title="Nothing is overdue">
            Every waiting approval is younger than {slaLabel(data.sla_minutes)}.
          </Nothing>
        ) : (
          <Table data-slot="sla-overdue">
            <TableCaption>Pending approvals older than the target, oldest first.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Approval</TableHead>
                <TableHead scope="col">Company</TableHead>
                <TableHead scope="col">Kind</TableHead>
                <TableHead scope="col">Approver seat</TableHead>
                <TableHead scope="col" className="text-right">
                  Waiting
                </TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.overdue.map((o) => (
                <TableRow
                  key={o.approval_id}
                  data-approval={o.approval_id}
                  className={TABLE_ROW_HOVER}
                >
                  <TableHead scope="row" className="max-w-80 font-normal whitespace-normal">
                    {o.summary ?? "No summary"}
                    <RowSub>Asked {formatDateTime(o.requested_at)}</RowSub>
                  </TableHead>
                  <TableCell>{o.company.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{humanize(o.kind)}</Badge>
                  </TableCell>
                  <TableCell>{o.approver_seat.title}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    <Badge variant="danger">{ageText(o.age_minutes)}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <RowActions>
                      <RowLinkAction
                        icon={ArrowUpRightIcon}
                        label={`Open approvals of ${o.company.name}`}
                        render={
                          <Link
                            to="/c/$slug/agents/approvals"
                            params={{ slug: o.company.slug }}
                            search={{ p: week }}
                          />
                        }
                      />
                    </RowActions>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section id="sla-companies" title="By company">
        <StatsTable
          slot="sla-companies"
          caption="Each company in its own week."
          first="Company"
          rows={data.companies.map((c) => ({
            key: c.company.id,
            name: c.company.name,
            sub: c.week_key,
            stats: c,
            actions: (
              <RowLinkAction
                icon={ArrowUpRightIcon}
                label={`Open approvals of ${c.company.name}`}
                render={
                  <Link
                    to="/c/$slug/agents/approvals"
                    params={{ slug: c.company.slug }}
                    search={{ p: week }}
                  />
                }
              />
            ),
          }))}
        />
      </Section>

      <Section id="sla-approvers" title="By approver seat">
        <StatsTable
          slot="sla-approvers"
          caption="Each seat that decides approvals; holders are the seat's active people."
          first="Approver seat"
          rows={data.approvers.map((a) => ({
            key: `${a.company.id}:${a.seat.id}`,
            name: a.seat.title,
            sub: `${a.company.name} · ${a.holders.length ? a.holders.join(", ") : "No holder"}`,
            stats: a,
            warn: a.holders.length === 0,
            actions: (
              <RowLinkAction
                icon={ArrowUpRightIcon}
                label={`Open seat ${a.seat.title} of ${a.company.name}`}
                render={
                  <Link
                    to="/c/$slug/kpis/chart/seat/$id"
                    params={{ slug: a.company.slug, id: a.seat.id }}
                  />
                }
              />
            ),
          }))}
        />
      </Section>
    </>
  );
}

function StatsTable({
  slot,
  caption,
  first,
  rows,
}: {
  slot: string;
  caption: string;
  first: string;
  rows: {
    key: string;
    name: string;
    sub: string;
    stats: Stats;
    warn?: boolean;
    actions?: ReactNode;
  }[];
}) {
  if (!rows.length) return <Nothing title="No rows" />;
  return (
    <Table data-slot={slot}>
      <TableCaption>{caption}</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">{first}</TableHead>
          <TableHead scope="col" className="text-right">
            Pending
          </TableHead>
          <TableHead scope="col" className="text-right">
            Over target
          </TableHead>
          <TableHead scope="col" className="text-right">
            Decided
          </TableHead>
          <TableHead scope="col" className="text-right">
            Within target
          </TableHead>
          <TableHead scope="col" className="text-right">
            Median
          </TableHead>
          <TableHead scope="col" className="text-right">
            P90
          </TableHead>
          <TableHead scope="col" className="text-right">
            Oldest waiting
          </TableHead>
          <TableHead scope="col">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.key} className={TABLE_ROW_HOVER}>
            <TableHead scope="row" className="font-normal">
              <div className="font-medium">{r.name}</div>
              <RowSub className={r.warn ? "text-warning-text" : undefined}>{r.sub}</RowSub>
            </TableHead>
            <TableCell className="text-right tabular-nums">{r.stats.pending}</TableCell>
            <TableCell className="text-right tabular-nums">
              {r.stats.pending_over_sla > 0 ? (
                <Badge variant="danger">{r.stats.pending_over_sla}</Badge>
              ) : (
                0
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">{r.stats.decided}</TableCell>
            <TableCell className="text-right tabular-nums">{withinPct(r.stats)}</TableCell>
            <TableCell className="text-right tabular-nums">
              {ageText(r.stats.median_minutes)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {ageText(r.stats.p90_minutes)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {ageText(r.stats.oldest_pending_minutes)}
            </TableCell>
            <TableCell className="text-right">
              {r.actions ? <RowActions>{r.actions}</RowActions> : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
